import { c as _c } from "react/compiler-runtime";
import { Status } from '@systemeio/components';
import { centsToUnits } from '@systemeio/util';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { Hint, Loader, Modal, Tooltip } from 'shared/components';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { getQuery } from 'shared/route-query/utils/get-query';
import { currencyFormat } from 'shared/utils/currency-format';
import { dateFormat } from 'shared/utils/date-time-format';
import { InvoiceErrorStatusEnum, invoiceErrorStatusLocoKeys } from '../enum/invoice-error-status-enum';
import { InvoiceStateColor, InvoiceStateEnum, InvoiceStateTitle } from '../enums/invoice-state-enum';
import { InvoiceWithdrawalMethodEnum } from '../enums/invoice-withdrawal-method-enum';
import { useInvoiceDetails } from '../hooks/use-invoice-details';
import { InvoiceDetailsInterface } from '../types/invoice-details-interface';
interface InvoiceDetailsInterfaceProps {
  invoiceId: number | null;
  onCloseModal: () => void;
}
type InvoiceError = InvoiceDetailsInterface['errors'][number];
const invoiceErrorStatusColor: Record<InvoiceErrorStatusEnum, Status.CircleColor> = {
  [InvoiceErrorStatusEnum.Failed]: 'error',
  [InvoiceErrorStatusEnum.ActionRequired]: 'warning',
  [InvoiceErrorStatusEnum.Cancelled]: 'inactive'
};
const WithdrawalMethod = t0 => {
  const $ = _c(3);
  const {
    details,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== details) {
    t1 = details.length ? <Tooltip label={<div className="flex flex-col items-start gap-2">{details.map(_temp)}</div>} triggerProps={{
      render: <button type="button" className="link" />
    }}>{children}</Tooltip> : <span className="text-text-secondary">{children}</span>;
    $[0] = children;
    $[1] = details;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const InvoiceInfoRow = t0 => {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <dt className="text-text-tertiary tablet:w-35 w-28 shrink-0">{label}</dt>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <dd className="text-text-primary min-w-0 flex-1">{children}</dd>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-baseline gap-4">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const InvoiceDetailsModal = t0 => {
  const $ = _c(31);
  const {
    invoiceId,
    onCloseModal
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const router = useRouter();
  const purchaseInvoiceId = router.query.purchaseInvoiceId as string;
  let t1;
  if ($[0] !== invoiceId) {
    t1 = {
      invoiceId
    };
    $[0] = invoiceId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    invoiceDetails
  } = useInvoiceDetails(t1);
  let t2;
  if ($[2] !== router.query) {
    t2 = getQuery(router.query, ["purchaseInvoiceId"]);
    $[2] = router.query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    unrelatedQuery
  } = t2;
  let t3;
  if ($[4] !== onCloseModal || $[5] !== purchaseInvoiceId || $[6] !== router || $[7] !== unrelatedQuery) {
    t3 = () => {
      onCloseModal();
      if (purchaseInvoiceId) {
        router.replace("/affiliate-dashboard", {
          query: unrelatedQuery
        }, {
          shallow: true
        });
      }
    };
    $[4] = onCloseModal;
    $[5] = purchaseInvoiceId;
    $[6] = router;
    $[7] = unrelatedQuery;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onClose = t3;
  let t4;
  if ($[9] !== user?.dateFormat || $[10] !== user?.timezone) {
    t4 = date => dateFormat(date, {
      dateFormat: user?.dateFormat,
      timeZone: user?.timezone
    });
    $[9] = user?.dateFormat;
    $[10] = user?.timezone;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const formatDate = t4;
  let t5;
  if ($[12] !== t) {
    t5 = error => <p className="text-text-tertiary text-sm"><TransLoco t={t} i18nKey="dashboard.affiliate_dashboard.payment_method" values={{
        method: t(InvoiceWithdrawalMethodEnum[error.withdrawalMethod])
      }} components={{
        method: <WithdrawalMethod details={Object.entries(error.withdrawalDetails)} />
      }} /></p>;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const renderPaymentMethod = t5;
  let t6;
  if ($[14] !== formatDate || $[15] !== renderPaymentMethod || $[16] !== t) {
    t6 = (error_0, index) => <li key={`${error_0.date}-${index}`} className="border-border-100 flex flex-col gap-2 border-t py-4"><div className="flex items-center justify-between gap-4"><Status color={invoiceErrorStatusColor[error_0.status]}>{t(invoiceErrorStatusLocoKeys[error_0.status])}</Status><span className="text-text-tertiary truncate text-sm">{formatDate(error_0.date)}</span></div><p className="text-text-primary">{error_0.errorMessage ? <TransLoco t={t} i18nKey="dashboard.affiliate.state.invoice_details.default_error_bank" values={{
          withdrawalMethod: t(InvoiceWithdrawalMethodEnum[error_0.withdrawalMethod]),
          errorMessage: error_0.errorMessage
        }} components={{
          b: <span className="font-medium" />
        }} /> : t("dashboard.affiliate.state.invoice_details.default_error_massage")}{error_0.errorMessageOriginal && <Hint inline={true} className="ml-2.5 align-text-bottom" label={error_0.errorMessageOriginal} />}</p>{renderPaymentMethod(error_0)}</li>;
    $[14] = formatDate;
    $[15] = renderPaymentMethod;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const renderError = t6;
  const t7 = !!invoiceId;
  let t8;
  if ($[18] !== t) {
    t8 = t("dashboard.affiliate.state.invoice_details.modal_title");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== formatDate || $[21] !== invoiceDetails || $[22] !== renderError || $[23] !== t || $[24] !== user?.dashboardLocale) {
    t9 = invoiceDetails ? <div className="flex flex-col gap-6"><dl className="bg-bg-secondary flex flex-col gap-3 rounded-lg p-4"><InvoiceInfoRow label={t("global.name")}>{invoiceDetails.purchaseInvoice.vendor.firstName}{" "}{invoiceDetails.purchaseInvoice.vendor.lastName}</InvoiceInfoRow><InvoiceInfoRow label={t("dashboard.affiliate.state.invoice_details.amount")}><span className="font-medium">{currencyFormat(centsToUnits(invoiceDetails.purchaseInvoice.amount), invoiceDetails.purchaseInvoice.currency, user?.dashboardLocale)}</span></InvoiceInfoRow><InvoiceInfoRow label={t("dashboard.affiliate.state.invoice_details.invoice_number")}>#{invoiceDetails.purchaseInvoice.number}</InvoiceInfoRow><InvoiceInfoRow label={t("dashboard.affiliate.state.invoice_details.invoice_status")}><Status color={InvoiceStateColor[InvoiceStateEnum.unpaid]}>{t(InvoiceStateTitle[InvoiceStateEnum.unpaid])}</Status></InvoiceInfoRow><InvoiceInfoRow label={t("global.date")}>{formatDate(invoiceDetails.purchaseInvoice.date)}</InvoiceInfoRow></dl>{!!invoiceDetails.errors.length && <section className="flex flex-col gap-3"><h3 className="text-text-primary text-xl font-medium">{t("dashboard.affiliate.state.invoice_details.history_title")}</h3><ul className="flex flex-col">{invoiceDetails.errors.map(renderError)}</ul></section>}</div> : <div className="m-auto table"><Loader type="dark" /></div>;
    $[20] = formatDate;
    $[21] = invoiceDetails;
    $[22] = renderError;
    $[23] = t;
    $[24] = user?.dashboardLocale;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== onClose || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <Modal opened={t7} onClose={onClose} title={t8}>{t9}</Modal>;
    $[26] = onClose;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
function _temp(t0) {
  const [key, value] = t0;
  return <span key={key}>{`${key}: ${value}`}</span>;
}